package com.rainbowclubs.rainbow.presentation.admin

import kotlinx.datetime.*
import org.jetbrains.compose.resources.StringResource
import rainbowclubs.composeapp.generated.resources.*
import kotlin.time.Instant

data class EventFormUiState(
    val title: String = "",
    val description: String = "",
    val location: String = "",
    val date: String = "",
    val endDate: String = "",
    val startsAt: String = "",
    val endsAt: String = "",
    val titleError: StringResource? = null,
    val descriptionError: StringResource? = null,
    val dateError: StringResource? = null,
    val endDateError: StringResource? = null,
    val startsAtError: StringResource? = null,
    val endsAtError: StringResource? = null,
    val validationError: StringResource? = null,
    val formError: StringResource? = null,
    val isSubmitting: Boolean = false,
    val isDeleting: Boolean = false,
    val deleteError: StringResource? = null,
)

data class EventTimestampValidation(
    val startsAt: Long,
    val endsAt: Long,
)

data class EventFormValidationResult(
    val form: EventFormUiState,
    val timestamps: EventTimestampValidation?,
)

fun validateEventForm(form: EventFormUiState): EventFormValidationResult {
    var updatedForm = form.copy(
        titleError = null,
        descriptionError = null,
        dateError = null,
        endDateError = null,
        startsAtError = null,
        endsAtError = null,
        validationError = null,
        formError = null,
        deleteError = null,
    )

    if (form.title.isBlank()) {
        updatedForm = updatedForm.copy(titleError = Res.string.field_required)
    }

    val startDate = if (form.date.isBlank()) {
        updatedForm = updatedForm.copy(dateError = Res.string.field_required)
        null
    } else {
        parseEventDate(form.date).also {
            if (it == null) {
                updatedForm = updatedForm.copy(dateError = Res.string.invalid_event_date)
            }
        }
    }

    val endDate = if (form.endDate.isBlank()) {
        updatedForm = updatedForm.copy(endDateError = Res.string.field_required)
        null
    } else {
        parseEventDate(form.endDate).also {
            if (it == null) {
                updatedForm = updatedForm.copy(endDateError = Res.string.invalid_event_date)
            }
        }
    }

    val startTime = if (form.startsAt.isBlank()) {
        updatedForm = updatedForm.copy(startsAtError = Res.string.field_required)
        null
    } else {
        parseEventTime(form.startsAt).also {
            if (it == null) {
                updatedForm = updatedForm.copy(startsAtError = Res.string.invalid_event_time)
            }
        }
    }

    val endTime = if (form.endsAt.isBlank()) {
        updatedForm = updatedForm.copy(endsAtError = Res.string.field_required)
        null
    } else {
        parseEventTime(form.endsAt).also {
            if (it == null) {
                updatedForm = updatedForm.copy(endsAtError = Res.string.invalid_event_time)
            }
        }
    }

    if (
        form.title.isBlank() ||
        form.date.isBlank() ||
        form.startsAt.isBlank() ||
        form.endDate.isBlank() ||
        form.endsAt.isBlank()
    ) {
        return EventFormValidationResult(
            form = updatedForm.copy(validationError = Res.string.event_required_fields_error),
            timestamps = null,
        )
    }

    if (startDate == null || endDate == null || startTime == null || endTime == null) {
        return EventFormValidationResult(
            form = updatedForm.copy(validationError = Res.string.event_invalid_fields_error),
            timestamps = null,
        )
    }

    val timezone = TimeZone.currentSystemDefault()
    val startsAt = LocalDateTime(startDate, startTime).toInstant(timezone).toEpochMilliseconds()
    val endsAt = LocalDateTime(endDate, endTime).toInstant(timezone).toEpochMilliseconds()

    if (startsAt >= endsAt) {
        return EventFormValidationResult(
            form = updatedForm.copy(
                endsAtError = Res.string.event_end_before_start_error,
                validationError = Res.string.event_end_before_start_error,
            ),
            timestamps = null,
        )
    }

    return EventFormValidationResult(
        form = updatedForm,
        timestamps = EventTimestampValidation(
            startsAt = startsAt,
            endsAt = endsAt,
        ),
    )
}

fun EventFormUiState.withEventValues(
    title: String,
    description: String,
    location: String?,
    startsAt: Long,
    endsAt: Long,
): EventFormUiState {
    return copy(
        title = title,
        description = description,
        location = location.orEmpty(),
        date = formatEventDate(startsAt),
        endDate = formatEventDate(endsAt),
        startsAt = formatEventTime(startsAt),
        endsAt = formatEventTime(endsAt),
        titleError = null,
        descriptionError = null,
        dateError = null,
        endDateError = null,
        startsAtError = null,
        endsAtError = null,
        validationError = null,
        formError = null,
        deleteError = null,
    )
}

fun formatEventDate(timestamp: Long): String {
    val dateTime = Instant.fromEpochMilliseconds(timestamp)
        .toLocalDateTime(TimeZone.currentSystemDefault())
    return "${dateTime.year}-${
        dateTime.month.ordinal.plus(1).toString().padStart(2, '0')
    }-${dateTime.day.toString().padStart(2, '0')}"
}

fun formatEventTime(timestamp: Long): String {
    val dateTime = Instant.fromEpochMilliseconds(timestamp)
        .toLocalDateTime(TimeZone.currentSystemDefault())
    return "${dateTime.hour.toString().padStart(2, '0')}:${
        dateTime.minute.toString().padStart(2, '0')
    }"
}

fun eventDateToEpochMillis(value: String): Long? {
    val date = parseEventDate(value) ?: return null
    return LocalDateTime(date, LocalTime(0, 0))
        .toInstant(TimeZone.currentSystemDefault())
        .toEpochMilliseconds()
}

fun parseEventDate(value: String): LocalDate? {
    val parts = value.trim().split("-")
    if (parts.size != 3) return null

    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null

    return try {
        LocalDate(year, month, day)
    } catch (_: IllegalArgumentException) {
        null
    }
}

fun parseEventTime(value: String): LocalTime? {
    val trimmed = value.trim()
    val normalized = trimmed.uppercase()
    val hasAmPm = normalized.endsWith("AM") || normalized.endsWith("PM")
    val timePart = if (hasAmPm) {
        normalized.removeSuffix("AM").removeSuffix("PM").trim()
    } else {
        normalized
    }

    val parts = timePart.split(":")
    val hourPart = parts.getOrNull(0)?.toIntOrNull() ?: return null
    val minutePart = parts.getOrNull(1)?.toIntOrNull() ?: 0

    val hour = if (hasAmPm) {
        val isPm = normalized.endsWith("PM")
        when {
            hourPart !in 1..12 -> return null
            isPm && hourPart != 12 -> hourPart + 12
            !isPm && hourPart == 12 -> 0
            else -> hourPart
        }
    } else {
        hourPart
    }

    return try {
        LocalTime(hour, minutePart)
    } catch (_: IllegalArgumentException) {
        null
    }
}
